import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import config from '@/config';
import useCache from '@/hooks/useCache';
import usePaymentProfile from '@/hooks/usePaymentProfile';
import useStateHandler from '@/hooks/useStateHandler';
import gallerySubscriptionService from '@/services/gallerySubscription.service';
import Loader from '@/shared-components/loader/Loader';
import GallerySubscriptionPaymentModal from '@/shared-components/popups/components/GallerySubscriptionPaymentModal';
import GallerySubscriptionPopup from '@/shared-components/popups/components/GallerySubscriptionPopup';
import { formatCurrencyAmount } from '@/utils/currency.utils';

import Header from './components/Header';
import Info from './components/Info';
import ProgressBar from './components/ProgressBar';

import './index.css';

const REQUIRED = 3;

function formatGalleryPriceHint(price, fallbackCurrency) {
  if (!price || typeof price !== 'object') {
    return null;
  }
  const amount = Number(price.amount ?? price.price ?? price.value ?? NaN);
  const totalWithVat = Number(
    price.totalWithVat ?? price.total_with_vat ?? price.total ?? NaN
  );
  const currency = price.currency ?? price.currencyCode ?? price.currency_code ?? fallbackCurrency ?? 'EUR';
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  const formattedAmount = formatCurrencyAmount(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (Number.isFinite(totalWithVat) && totalWithVat > amount) {
    const formattedTotalWithVat = formatCurrencyAmount(totalWithVat, currency, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (!formattedAmount || !formattedTotalWithVat) {
      return null;
    }
    return `${formattedAmount} + VAT -> ${formattedTotalWithVat}/month — cancel anytime`;
  }
  if (!formattedAmount) {
    return null;
  }
  return `${formattedAmount}/month — cancel anytime`;
}

function formatMonthlyPrice(price, fallbackCurrency) {
  if (!price || typeof price !== 'object') {
    return null;
  }
  const amount = Number(price.amount ?? price.price ?? price.value ?? NaN);
  const currency = price.currency ?? price.currencyCode ?? price.currency_code ?? fallbackCurrency ?? 'EUR';
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  const formattedAmount = formatCurrencyAmount(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (!formattedAmount) {
    return null;
  }
  return `${formattedAmount}/month`;
}

export default function VerifyGallery() {
  const [internalLoading, setInternalLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [gallerySubscriptionPopupOpen, setGallerySubscriptionPopupOpen] = useState(false);
  const [subscriptionConfig, setSubscriptionConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const { cacheHandler } = useStateHandler();
  const paymentProfile = usePaymentProfile();
  const refreshPaymentProfile = paymentProfile?.refreshProfile;

  // keep setFetchData for children using Outlet context
  const { loading, fetchData, setFetchData } = useCache(
    'gallery_onboarding_data',
    `${config.apis.api.url}/onboarding/artwork/info`,
    {
      injectToken: true,
      type: '@cache/persist',
      invalidateWhen: ['ARTWORK_DRAFT_SAVED', 'NEW_ARTWORK_REQUEST'],
    }
  );

  const navigate = useNavigate();

  const verifyProfileAndContinueToOnboarding = () => {
    cacheHandler.storeInCache('canUserCompleteOnboarding', true, {
      type: '@cache/no-expiration',
    });
    navigate('/onboarding/gallery', { replace: true });
  };

  // ✅ Derive counts correctly:
  // - uploadedCount from drafts (everything except "incomplete")
  // - verifiedCount from artworks (or artworks_count if backend provides it)
  const { uploadedCount, verifiedCount } = useMemo(() => {
    const drafts = fetchData?.drafts || [];
    const artworks = fetchData?.artworks || [];
    const norm = (s) => (s || '').toString().toLowerCase();

    const isUploaded = (d) => norm(d.draft_status) !== 'incomplete';
    const uploaded = drafts.filter(isUploaded).length;

    const isVerified = (a) => {
      const s = norm(a.draft_status || a.status || a.artwork_status);
      return s === 'approved' || s === 'accepted' || s === 'verified';
    };

    const verified =
      typeof fetchData?.artworks_count === 'number'
        ? fetchData.artworks_count
        : artworks.filter(isVerified).length;

    return { uploadedCount: uploaded, verifiedCount: verified };
  }, [fetchData]);

  // Auto-advance only when 3 VERIFIED
  useEffect(() => {
    if (!loading && (fetchData?.artworks_count ?? verifiedCount) >= REQUIRED) {
      verifyProfileAndContinueToOnboarding();
    } else if (!loading) {
      setInternalLoading(false);
    }
  }, [loading, verifiedCount, fetchData?.artworks_count]);

  const refreshSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const { response, data } = await gallerySubscriptionService.getMine();
      if (response?.ok) {
        setSubscription(data);
      } else {
        setSubscription(null);
      }
    }
    catch {
      setSubscription(null);
    }
    finally {
      setSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  useEffect(() => {
    let isMounted = true;
    setConfigLoading(true);
    gallerySubscriptionService.getConfig()
      .then(({ response, data }) => {
        if (!isMounted) {
          return;
        }
        if (response?.ok) {
          setSubscriptionConfig(data);
          setConfigError(null);
        }
        else {
          setConfigError(data?.message ?? 'Unable to load subscription price.');
        }
      })
      .catch(() => {
        if (isMounted) {
          setConfigError('Unable to load subscription price.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setConfigLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const hasConfirmedSubscription = subscription?.status === 'confirmed';
  const hasTrialSubscription = Boolean(
    subscription?.status === 'trialing' ||
    subscription?.trialing === true ||
    subscription?.metadata?.trial_active === true
  );
  const hasActiveSubscription = hasConfirmedSubscription || hasTrialSubscription;
  const subscriptionState = hasTrialSubscription
    ? 'trialing'
    : hasConfirmedSubscription
      ? 'confirmed'
      : 'pending';

  const handleAddArtwork = useCallback(() => {
    if (hasActiveSubscription) {
      navigate('/verify-gallery/add-artwork', { state: { from: true } });
    }
    else {
      setGallerySubscriptionPopupOpen(true);
    }
  }, [hasActiveSubscription, navigate]);

  const handleResetSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    try {
      const { response, data } = await gallerySubscriptionService.cancelPayment();
      if (response?.ok) {
        setSubscription(data);
      }
      else {
        setSubscription(null);
      }
    }
    catch {
      setSubscription(null);
    }
    finally {
      setSubscriptionLoading(false);
      refreshSubscription();
      if (typeof refreshPaymentProfile === 'function') {
        refreshPaymentProfile();
      }
    }
  }, [refreshPaymentProfile, refreshSubscription]);

  const handlePaymentSuccess = useCallback((payload) => {
    if (payload) {
      setSubscription(payload);
    }
    else {
      refreshSubscription();
    }
    setPaymentModalOpen(false);
    setGallerySubscriptionPopupOpen(false);
    if (typeof refreshPaymentProfile === 'function') {
      refreshPaymentProfile();
    }
    window.location.reload();
  }, [refreshSubscription, refreshPaymentProfile]);

  const handleGallerySubscriptionConfirm = useCallback(() => {
    setGallerySubscriptionPopupOpen(false);
    setPaymentModalOpen(true);
  }, []);

  const paymentHint = useMemo(() => {
    if (configLoading) {
      return 'Loading subscription price.';
    }
    if (configError) {
      return configError;
    }
    const reducedPlan = subscriptionConfig?.plans?.reduced ?? null;
    const priceHint = formatGalleryPriceHint(reducedPlan, subscriptionConfig?.currency ?? 'EUR');
    if (priceHint) {
      return priceHint;
    }
    return 'Stripe will show the subscription price before you confirm payment.';
  }, [configError, configLoading, subscriptionConfig]);

  const pricingPromo = useMemo(() => {
    if (configLoading || configError || !subscriptionConfig?.plans) {
      return null;
    }
    const reducedPlan = subscriptionConfig.plans.reduced ?? null;
    const standardPlan = subscriptionConfig.plans.standard ?? null;
    const reducedLabel = formatMonthlyPrice(reducedPlan, subscriptionConfig?.currency ?? 'EUR');
    const standardLabel = formatMonthlyPrice(standardPlan, subscriptionConfig?.currency ?? 'EUR');
    if (!reducedLabel || !standardLabel) {
      return null;
    }
    return {
      reducedLabel,
      standardLabel,
    };
  }, [configError, configLoading, subscriptionConfig]);

  const seatPricingNote = useMemo(() => {
    const seatsConfig = subscriptionConfig?.seats ?? null;
    if (!seatsConfig) {
      return null;
    }
    const included = Number(seatsConfig.included ?? 1);
    const unitPrice = Number(seatsConfig.price ?? NaN);
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
      return null;
    }
    const formattedSeatPrice = formatCurrencyAmount(
      unitPrice,
      subscriptionConfig?.currency ?? 'EUR',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        euroSymbolAfter: false,
      }
    );
    if (!formattedSeatPrice) {
      return null;
    }
    if (included > 1) {
      return `${included} members included. Additional seats are ${formattedSeatPrice} + VAT per member per month.`;
    }
    return `First member free. Additional seats are ${formattedSeatPrice} + VAT per member per month.`;
  }, [subscriptionConfig]);

  // Label under the bar (only while verified < REQUIRED)
  const progressLabel =
    verifiedCount < REQUIRED
      ? uploadedCount < REQUIRED
        ? `${uploadedCount} of ${REQUIRED} artworks uploaded`
        : `All set! ${REQUIRED}/${REQUIRED} uploaded. Waiting for verification (${verifiedCount}/${REQUIRED} verified)`
      : '';

  return internalLoading ? (
    <div className="verify-gallery__loading-page">
      <Loader className="verify-gallery__spinner" thin />
    </div>
  ) : (
    <div className="verify-gallery__container">
      {/* Header no longer shows the CTA */}
      <Header
        artworksCount={verifiedCount}
        onBeginClick={verifyProfileAndContinueToOnboarding}
        paymentLoading={subscriptionLoading}
        hasPaymentMethod={hasActiveSubscription}
        subscriptionState={subscriptionState}
      />

      <div className="verify-gallery__wrap">
        {/* Info renders either Add artwork or Begin my journey */}
        <Info
          verifiedCount={verifiedCount}
          required={REQUIRED}
          onBeginClick={verifyProfileAndContinueToOnboarding}
          onAddArtwork={handleAddArtwork}
          canAddArtwork={hasActiveSubscription}
          paymentLoading={subscriptionLoading}
          paymentHint={paymentHint}
          pricingPromo={pricingPromo}
          showResetSubscriptionButton={hasActiveSubscription}
          onResetSubscription={handleResetSubscription}
        />

        {/* Progress only while NOT all verified */}
        {verifiedCount < REQUIRED && (
          <ProgressBar
            current={Math.min(uploadedCount, REQUIRED)} // progress = uploads
            total={REQUIRED}
            label={progressLabel}
          />
        )}

        <div className="verify-gallery__data-wrap">
          <Outlet context={{ fetchData, setFetchData }} />
        </div>
      </div>

      <GallerySubscriptionPopup
        open={gallerySubscriptionPopupOpen}
        onClose={() => setGallerySubscriptionPopupOpen(false)}
        onConfirm={handleGallerySubscriptionConfirm}
        priceHint={paymentHint}
        pricingPromo={pricingPromo}
        seatNote={seatPricingNote}
        error={configError}
        isLoading={configLoading}
      />

      <GallerySubscriptionPaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
        priceHint={paymentHint}
      />
    </div>
  );
}
